import { useEffect, useState, useSyncExternalStore } from "react";
import { useLocaleSafe } from "@/contexts/LocaleContext";
import { RefreshCw } from "lucide-react";
import {
  hasPendingFinancialTransport,
  subscribeFinancialTransport,
} from "@/lib/financialTransport";

/**
 * بانر يظهر عند فشل تحميل chunk JS بعد محاولة reload تلقائيّة أولى.
 * يعرض زرّ «حدّث الصفحة» يفرض إعادة تحميل صلب مع cache-bust.
 */
export function ChunkErrorBanner() {
  const { locale } = useLocaleSafe();
  const [visible, setVisible] = useState(false);
  const financialPending = useSyncExternalStore(
    subscribeFinancialTransport,
    hasPendingFinancialTransport,
    () => false,
  );

  useEffect(() => {
    const onError = () => {
      const fallback = document.getElementById("lecmo-slow-load-fallback");
      if (fallback) fallback.remove();
      if (document.body) {
        document.body.dataset.lecmoReady = "1";
      }
      setVisible(true);
    };
    window.addEventListener("lecmo:chunk-error", onError);
    return () => window.removeEventListener("lecmo:chunk-error", onError);
  }, []);

  if (!visible) return null;

  const isAr = locale === "ar";
  const title = isAr ? "تحديث جديد متاح" : "A new update is available";
  const body = financialPending
    ? isAr
      ? "جارٍ التحقق من تأكيد استلام المبلغ. ابقَ في هذه الصفحة ولا تحدّثها أو تعِد المحاولة."
      : "Payment confirmation is still being checked. Stay on this page. Do not refresh or retry."
    : isAr
      ? "حدث تحديث للموقع. حدّث الصفحة للمتابعة."
      : "The site was updated. Please refresh to continue.";
  const cta = financialPending
    ? isAr
      ? "يرجى الانتظار"
      : "Please wait"
    : isAr
      ? "حدّث الصفحة"
      : "Refresh page";

  const handleReload = () => {
    // Recheck at click time as a transport can begin after the last render.
    if (hasPendingFinancialTransport()) return;
    try {
      sessionStorage.removeItem("__lecmo_chunk_reloaded__");
    } catch {
      /* noop */
    }
    const url = new URL(window.location.href);
    url.searchParams.set("_v", String(Date.now()));
    window.location.replace(url.toString());
  };

  return (
    <div
      role="alert"
      className="fixed inset-x-3 bottom-3 z-[100] mx-auto max-w-md rounded-xl border border-border bg-background/95 p-4 shadow-lg backdrop-blur-md sm:inset-x-auto sm:end-4 sm:start-auto"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <p className="mt-1 text-xs text-muted-foreground">{body}</p>
        </div>
        <button
          type="button"
          disabled={financialPending}
          onClick={handleReload}
          className="inline-flex min-h-[36px] items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground transition hover:opacity-90"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {cta}
        </button>
      </div>
    </div>
  );
}
